/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Mon Aug 12 2024
 **/
import { _T } from "@rfice/language";

import { NotificationItem, MyDeskNotificationItem } from "./atom";
import { AlarmLogMessageType } from "./types";

export type MydeskAvatarReturnType = Required<MyDeskNotificationItem["avatar"]>;
export type UserInfoReturnType = Required<MyDeskNotificationItem["userInfo"]>;
export function formatMydeskGroupInfoByAvatar(info: PushServices.GroupInfo): MydeskAvatarReturnType {
	return {
		type: "group",
		name: info.name,
		imageSrc: info.logoPath,
		colorLogo: info.colorLogo && `${info.colorLogo}_50_100`
	};
}

export function formatMydeskSpaceInfoByAvatar(info: PushServices.SpaceInfo): MydeskAvatarReturnType {
	return {
		type: "space",
		name: info.name?.slice(0, 1) || "",
		imageSrc: info.thumbnailPath,
		colorLogo: undefined
	};
}

export function formatMydeskUserInfoByAvatar(info: PushServices.UserInfo): MydeskAvatarReturnType {
	return {
		type: "user",
		name: info.name?.slice(0, 1) || "",
		imageSrc: undefined,
		colorLogo: undefined
	};
}

export function formatMyDeskAlarmData(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	message: NotificationItem
): MyDeskNotificationItem | undefined {
	switch (message.type) {
		case AlarmLogMessageType.SPACE_JOIN_REQUEST: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 가입요청"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				spaceId: payload.spaceInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.SPACE_ACCEPT_JOIN_REQUEST: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 가입완료"),
				subTitle: _T("지금 입장해보세요!"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				spaceId: payload.spaceInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.SPACE_INVITED: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 초대"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				userInfo: payload.sender,
				isRead: message.read,
				createdAt: message.createdTime,
				spaceId: payload.spaceInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.SPACE_INVITE_PROCESSED: {
			const { payload, type, id } = message;
			if (message.payload.approved) {
				return {
					title: _T("스페이스 초대수락"),
					name: payload.spaceInfo.name,
					avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
					userInfo: payload.userInfo,
					isRead: message.read,
					createdAt: message.createdTime,
					spaceId: payload.spaceInfo.id,
					id: id,
					type: type,
					approved: message.payload.approved,
					readKey: payload.readKey
				};
			} else {
				return {
					title: _T("스페이스 초대거절"),
					name: payload.spaceInfo.name,
					avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
					userInfo: payload.userInfo,
					isRead: message.read,
					createdAt: message.createdTime,
					spaceId: payload.spaceInfo.id,
					id: id,
					type: type,
					approved: message.payload.approved,
					readKey: payload.readKey
				};
			}
		}
		case AlarmLogMessageType.SPACE_USER_DELETED_BY_ADMIN: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 멤버삭제"),
				subTitle: _T("스페이스 멤버에서 삭제되었어요"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.SPACE_DELETED: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 삭제"),
				subTitle: _T("더이상 이용이 불가합니다"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				spaceId: payload.spaceInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.OPEN_WORLD_BLOCKED_USER_PARTICIPATED: {
			const { payload, type, id } = message;
			return {
				title: _T("차단한 사용자"),
				subTitle: _T("오픈월드에 차단한 사용자가 있어요"),
				icon: "BlockMark24",
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_INVITED: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 초대"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				userInfo: payload.sender,
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.groupInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_INVITE_PROCESSED: {
			const { payload, type, id } = message;
			if (message.payload.approved) {
				return {
					title: _T("그룹 초대수락"),
					name: payload.groupInfo.name,
					avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
					userInfo: payload.userInfo,
					isRead: message.read,
					createdAt: message.createdTime,
					groupId: payload.groupInfo.id,
					id: id,
					type: type,
					subTitle: _T("그룹 초대를 수락하였습니다."),
					readKey: payload.readKey,
					approved: message.payload.approved
				};
			} else {
				return {
					title: _T("그룹 초대거절"),
					name: payload.groupInfo.name,
					avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
					userInfo: payload.userInfo,
					isRead: message.read,
					createdAt: message.createdTime,
					groupId: payload.groupInfo.id,
					id: id,
					type: type,
					subTitle: _T("그룹 초대를 거절하였습니다."),
					readKey: payload.readKey,
					approved: message.payload.approved
				};
			}
		}
		case AlarmLogMessageType.GROUP_JOIN_REQUEST: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 가입요청"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.groupInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_ACCEPT_JOIN_REQUEST: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 가입 수락"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.groupInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_REJECT_JOIN_REQUEST: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 가입거절"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.groupInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_JOINED: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 가입완료"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.groupInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_ACCEPT_DELETE_REQUEST: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 탈퇴"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.groupInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_USER_DELETED_BY_ADMIN: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 멤버삭제"),
				subTitle: _T("그룹멤버에서 삭제되었어요"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.FRIEND_REQUESTED: {
			const { payload, type, id } = message;
			return {
				title: _T("친구 맺기"),
				subTitle: _T("반갑게 인사해요"),
				name: payload.userInfo.name,
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.SPACE_JOIN_REQUEST_CANCEL: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 가입요청 취소"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				spaceId: payload.spaceInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.SPACE_JOINED: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 가입완료"),
				subTitle: _T("지금 입장해보세요!"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				isRead: message.read,
				createdAt: message.createdTime,
				spaceId: payload.spaceInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.SPACE_ADMIN_ASSIGNED: {
			const { payload, type, id } = message;
			return {
				title: _T("스페이스 관리자지정"),
				subTitle: _T("스페이스 관리자로 지정되었어요"),
				name: payload.spaceInfo.name,
				avatar: formatMydeskSpaceInfoByAvatar(payload.spaceInfo),
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.spaceInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GROUP_ADMIN_ASSIGNED: {
			const { payload, type, id } = message;
			return {
				title: _T("그룹 관리자지정"),
				subTitle: _T("그룹 관리자로 지정되었어요"),
				name: payload.groupInfo.name,
				avatar: formatMydeskGroupInfoByAvatar(payload.groupInfo),
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				groupId: payload.groupInfo.id,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.GUEST_BOOK_POSTED: {
			const { payload, type, id } = message;
			return {
				title: _T("마이룸"),
				subTitle: _T("방명록을 남겼어요"),
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.HANGUP_NO_RESPONSE_CALL: {
			const { payload, type, id } = message;
			return {
				title: _T("부재중 전화"),
				subTitle: _T("부재중 전화가 수신되었습니다."),
				icon: "CallMissedPhoneThick24",
				isRead: message.read,
				userInfo: payload.users,
				callId: payload.callId,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.CHAT_NON_VOTE_NOTIFICATION: {
			const { payload, type, id } = message;
			return {
				title: _T("미참여 투표"),
				subTitle: _T("참여하지 않은 투표가 있어요"),
				icon: "Vote24",
				isRead: message.read,
				createdAt: message.createdTime,
				chatType: payload.type,
				name: payload.spaceName,
				roomName: payload.roomName,
				userInfo: payload.receivers,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.KNOCK: {
			const { payload, type, id } = message;
			return {
				title: _T("부재중 노크"),
				userInfo: payload.userInfo,
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.MEETING_SCHEDULE_INVITE:
		case AlarmLogMessageType.MEETING_CONFERENCE_INVITE: {
			const { payload, type, id } = message;
			return {
				title: _T("미팅 초대"),
				subTitle: _T("일정을 확인하세요"),
				icon: "CalendarMonth24",
				meetingName: payload.title,
				conferenceId: payload.conferenceId,
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
		case AlarmLogMessageType.MEETING_SCHEDULE_REMIND: {
			const { payload, type, id } = message;
			return {
				title: _T("미팅 시작"),
				subTitle: _T("10분전입니다. 미리 준비하세요!"),
				icon: "CalendarMonth24",
				meetingName: payload.title,
				conferenceId: payload.conferenceId,
				isRead: message.read,
				createdAt: message.createdTime,
				id: id,
				type: type,
				readKey: payload.readKey
			};
		}
	}
}
export function formatMydeskSerializedData(res: {
	id: string;
	createdTime: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload: any;
	read: boolean;
	type: string; // keyof AlarmLogMessageValueType;
}): NotificationItem {
	return {
		...res,
		createdTime: res.createdTime,
		type: AlarmLogMessageType[res.type as keyof typeof AlarmLogMessageType]
	};
}
